package dev.kuml.portal.web.i18n.strings

/**
 * R-18 §7 -- decision E-9: the server's `ErrorResponse.message` is NEVER rendered in the UI
 * anymore (it is simply discarded -- see call sites in `error/ErrorCopy.kt` and every screen
 * that used to fall back to `result.error?.message`; nothing in `kuml-portal-web/src/jsMain`
 * writes it to `console.warn` or any other diagnostic sink today). Every user-visible error string
 * is picked from here by (status, code) via [forCode], or by the fixed-status helpers below for
 * the generation flow's well-known statuses. The one exception is [compilerDetailPrefix], prefixed
 * to the kUML compiler's own (untranslatable) 422 diagnostic text.
 */
public interface ErrorStrings {
    public val sessionExpired: String
    public val projectNotFound: String
    public val serviceUnavailable: String
    public val networkError: String
    public val validationRejected: String
    public val compilerDetailPrefix: String

    public fun rateLimited(retryAfterSeconds: Long?): String

    public fun quotaExhausted(
        limit: Int?,
        resetLabel: String?,
    ): String

    /**
     * Maps a server-issued `code` (see this wave's plan §7.3's table) to its translated,
     * client-safe text -- `null` if [code] is unrecognised or absent, in which case the caller
     * falls back to its own generic per-status copy. [status] is currently unused by every branch
     * but kept in the signature so a future code that legitimately differs by status (there is
     * none today) does not need a breaking signature change.
     */
    public fun forCode(
        code: String?,
        status: Int,
    ): String?

    public val allPlainStrings: List<String>
        get() = listOf(sessionExpired, projectNotFound, serviceUnavailable, networkError, validationRejected, compilerDetailPrefix)
}

/**
 * `internal`, not `private` -- so [dev.kuml.portal.web.i18n.strings.ErrorStringsKeyParityTest]
 * (jsTest) can assert that this and [errorCodeMessagesEn] share EXACTLY the same key set (see
 * [ErrorResponse][dev.kuml.portal.server.error.ErrorResponse]'s KDoc: a code that lands in only
 * one language's map silently degrades that language to the generic per-status copy, with
 * otherwise no test to catch the drift).
 */
internal fun errorCodeMessagesDe(): Map<String, String> =
    mapOf(
        "display_name_empty" to "Bitte geben Sie einen Anzeigenamen ein.",
        "display_name_too_long" to "Der Anzeigename ist zu lang.",
        "display_name_invalid_characters" to "Der Anzeigename enthält nicht erlaubte Zeichen.",
        "email_invalid" to "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
        "email_too_long" to "Die E-Mail-Adresse ist zu lang.",
        "password_too_short" to "Das Passwort muss mindestens 8 Zeichen lang sein.",
        "password_too_long" to "Das Passwort ist zu lang.",
        "too_many_requests" to "Zu viele Anfragen in kurzer Zeit. Bitte versuchen Sie es gleich erneut.",
        "payload_too_large" to "Die Anfrage ist zu groß.",
        "team_limit_reached" to "Das Team-Limit Ihres Plans ist erreicht.",
        "team_member_limit_reached" to "Das Mitglieder-Limit dieses Teams ist erreicht.",
        "avatar_empty" to "Die hochgeladene Datei ist leer.",
        "avatar_too_large" to "Das Bild überschreitet die zulässige Größe.",
        "avatar_unsupported_format" to "Es werden nur PNG-, JPEG- und WebP-Bilder akzeptiert.",
        "avatar_unreadable" to "Die Bilddatei konnte nicht gelesen werden.",
        "avatar_dimensions" to "Das Bild hat eine nicht zulässige Größe.",
        "ai_context_unsupported_format" to "Nicht unterstütztes Dateiformat. Erlaubt sind CSV, JSON, Markdown und Textdateien.",
        "ai_context_empty" to "Die hochgeladene Datei ist leer.",
        "ai_context_too_large" to "Die Datei ist zu groß.",
        "ai_context_invalid_encoding" to "Die Datei muss UTF-8-kodierter Text sein.",
        "ai_context_control_characters" to "Die Datei enthält Steuerzeichen und sieht nicht wie Text aus.",
        "ai_context_too_many_characters" to "Die Datei überschreitet die zulässige Zeichenanzahl.",
        "ai_context_invalid_json" to "Die Datei ist kein gültiges JSON.",
        "ai_context_store_full" to
            "Es können derzeit keine weiteren Kontextdateien angenommen werden. Bitte versuchen Sie es gleich erneut.",
        "ai_context_unavailable" to "Die angehängte Datei ist abgelaufen. Bitte hängen Sie sie erneut an.",
        "report_text_empty" to "Der Meldungstext darf nicht leer sein.",
        "report_text_too_long" to "Der Meldungstext ist zu lang.",
        "report_text_invalid_characters" to "Der Meldungstext enthält ein nicht unterstütztes Zeichen.",
        "generation_already_reported" to "Diese Generierung wurde bereits gemeldet.",
        "generation_comment_conflict" to "Der Kommentar konnte nicht gespeichert werden.",
        "project_limit_reached" to "Das Projekt-Limit Ihres Plans ist erreicht.",
        "ai_monthly_limit_reached" to "Ihr monatliches KI-Kontingent ist aufgebraucht.",
        "diagram_limit_reached" to "Free-Plan: maximal 3 Diagramme je Projekt. Löschen Sie ein Diagramm, um Platz zu schaffen.",
        "insufficient_role" to "Ihre Rolle reicht für diese Aktion nicht aus.",
        "last_diagram" to "Ein Projekt braucht mindestens ein Diagramm. Löschen Sie stattdessen das Projekt.",
        "diagram_live_room" to "In diesem Diagramm wird gerade gemeinsam gearbeitet.",
        "share_link_limit_reached" to "Für dieses Diagramm gibt es bereits die maximal mögliche Anzahl an Freigabe-Links.",
        "share_diagram_empty" to "Dieses Diagramm hat noch keinen Inhalt und kann noch nicht geteilt werden.",
        "share_expiry_invalid" to "Das Ablaufdatum muss in der Zukunft liegen.",
        "share_expiry_too_far" to "Das Ablaufdatum liegt zu weit in der Zukunft.",
        "share_password_required" to "Dieser Link ist passwortgeschützt.",
        "share_password_wrong" to "Das Passwort ist nicht korrekt.",
        "share_not_found" to "Dieser Link ist ungültig, abgelaufen oder wurde widerrufen.",
        "share_render_failed" to "Dieses Diagramm kann derzeit nicht dargestellt werden.",
        "share_store_full" to "Es können derzeit keine weiteren Freigaben geöffnet werden. Bitte versuchen Sie es gleich erneut.",
        "oauth_last_login_method" to
            "Dies ist Ihre einzige Anmeldemethode. Legen Sie zuerst ein Passwort fest oder verknüpfen Sie ein " +
            "weiteres Konto, bevor Sie dieses trennen.",
        "project_file_empty" to "Die hochgeladene Datei ist leer.",
        "project_file_too_large" to "Die Datei überschreitet die zulässige Größe.",
        "project_file_unsupported_extension" to "Nicht unterstütztes Dateiformat.",
        "project_file_kind_mismatch" to "Der Dateiinhalt passt nicht zur Dateiendung.",
        "project_file_invalid_encoding" to "Die Datei muss UTF-8-kodierter Text sein.",
        "project_file_control_characters" to "Die Datei enthält Steuerzeichen und sieht nicht wie Text aus.",
        "project_file_too_many_characters" to "Die Datei überschreitet die zulässige Zeichenanzahl.",
        "project_file_invalid_json" to "Die Datei ist kein gültiges JSON.",
        "project_file_unreadable" to "Die Datei konnte nicht gelesen werden.",
        "project_file_dimensions" to "Das Bild hat eine nicht zulässige Größe.",
        "project_file_active_svg_content" to "Diese SVG-Datei enthält nicht erlaubte aktive Inhalte.",
        "project_file_invalid_name" to "Der Dateiname ist ungültig.",
        "project_file_invalid_description" to "Die Beschreibung ist ungültig oder zu lang.",
        "project_file_name_taken" to "Eine Datei mit diesem Namen existiert in diesem Projekt bereits.",
        "project_file_limit_reached" to "Das Datei-Limit Ihres Plans für dieses Projekt ist erreicht.",
        "project_file_storage_full" to "Das Speicherkontingent Ihres Plans für dieses Projekt ist erreicht.",
    )

/** `internal` counterpart of [errorCodeMessagesDe] -- see that function's KDoc. */
internal fun errorCodeMessagesEn(): Map<String, String> =
    mapOf(
        "display_name_empty" to "Please enter a display name.",
        "display_name_too_long" to "The display name is too long.",
        "display_name_invalid_characters" to "The display name contains disallowed characters.",
        "email_invalid" to "Please enter a valid email address.",
        "email_too_long" to "The email address is too long.",
        "password_too_short" to "The password must be at least 8 characters.",
        "password_too_long" to "The password is too long.",
        "too_many_requests" to "Too many requests in a short time. Please try again shortly.",
        "payload_too_large" to "The request is too large.",
        "team_limit_reached" to "Your plan's team limit has been reached.",
        "team_member_limit_reached" to "This team's member limit has been reached.",
        "avatar_empty" to "The uploaded file is empty.",
        "avatar_too_large" to "The image exceeds the allowed size.",
        "avatar_unsupported_format" to "Only PNG, JPEG, and WebP images are accepted.",
        "avatar_unreadable" to "The image file could not be read.",
        "avatar_dimensions" to "The image has a disallowed size.",
        "ai_context_unsupported_format" to "Unsupported file format. Allowed: CSV, JSON, Markdown, and plain text.",
        "ai_context_empty" to "The uploaded file is empty.",
        "ai_context_too_large" to "The file is too large.",
        "ai_context_invalid_encoding" to "The file must be UTF-8 encoded text.",
        "ai_context_control_characters" to "The file contains control characters and does not look like text.",
        "ai_context_too_many_characters" to "The file exceeds the allowed character count.",
        "ai_context_invalid_json" to "The file is not valid JSON.",
        "ai_context_store_full" to "No further context files can be accepted right now. Please try again shortly.",
        "ai_context_unavailable" to "The attached file has expired. Please attach it again.",
        "report_text_empty" to "The report text must not be empty.",
        "report_text_too_long" to "The report text is too long.",
        "report_text_invalid_characters" to "The report text contains an unsupported character.",
        "generation_already_reported" to "This generation has already been reported.",
        "generation_comment_conflict" to "The comment could not be saved.",
        "project_limit_reached" to "Your plan's project limit has been reached.",
        "ai_monthly_limit_reached" to "Your monthly AI quota is used up.",
        "diagram_limit_reached" to "Free plan: up to 3 diagrams per project. Delete a diagram to make room.",
        "insufficient_role" to "Your role does not allow this action.",
        "last_diagram" to "A project needs at least one diagram. Delete the project instead.",
        "diagram_live_room" to "Someone is currently collaborating on this diagram.",
        "share_link_limit_reached" to "This diagram already has the maximum possible number of share links.",
        "share_diagram_empty" to "This diagram has no content yet and cannot be shared yet.",
        "share_expiry_invalid" to "The expiry date must be in the future.",
        "share_expiry_too_far" to "The expiry date is too far in the future.",
        "share_password_required" to "This link is password-protected.",
        "share_password_wrong" to "That password is incorrect.",
        "share_not_found" to "This link is invalid, expired, or has been revoked.",
        "share_render_failed" to "This diagram cannot currently be displayed.",
        "share_store_full" to "No further shared links can be unlocked right now. Please try again shortly.",
        "oauth_last_login_method" to
            "This is your only sign-in method. Set a password or link another account before disconnecting this one.",
        "project_file_empty" to "The uploaded file is empty.",
        "project_file_too_large" to "The file exceeds the allowed size.",
        "project_file_unsupported_extension" to "Unsupported file format.",
        "project_file_kind_mismatch" to "The file's content does not match its extension.",
        "project_file_invalid_encoding" to "The file must be UTF-8 encoded text.",
        "project_file_control_characters" to "The file contains control characters and does not look like text.",
        "project_file_too_many_characters" to "The file exceeds the allowed character count.",
        "project_file_invalid_json" to "The file is not valid JSON.",
        "project_file_unreadable" to "The file could not be read.",
        "project_file_dimensions" to "The image has a disallowed size.",
        "project_file_active_svg_content" to "This SVG file contains disallowed active content.",
        "project_file_invalid_name" to "The file name is invalid.",
        "project_file_invalid_description" to "The description is invalid or too long.",
        "project_file_name_taken" to "A file with this name already exists in this project.",
        "project_file_limit_reached" to "Your plan's file limit for this project has been reached.",
        "project_file_storage_full" to "Your plan's storage limit for this project has been reached.",
    )

public object ErrorStringsDe : ErrorStrings {
    override val sessionExpired = "Ihre Sitzung ist abgelaufen. Bitte erneut anmelden."
    override val projectNotFound = "Projekt nicht gefunden."
    override val serviceUnavailable = "Die KI-Funktion ist derzeit nicht verfügbar. Bitte versuchen Sie es später erneut."
    override val networkError = "Verbindung fehlgeschlagen. Ihr Diagramm ist unverändert — bitte erneut versuchen."
    override val validationRejected =
        "Aus dieser Beschreibung ließ sich kein gültiges Diagramm erzeugen. Ihr bisheriges Diagramm " +
            "bleibt unverändert. Versuchen Sie es mit einer klareren oder einfacheren Beschreibung."
    override val compilerDetailPrefix = "Meldung des Diagramm-Compilers:"

    private val codeMessages = errorCodeMessagesDe()

    override fun rateLimited(retryAfterSeconds: Long?): String =
        "Einen Moment bitte — zu viele Anfragen in kurzer Zeit. In ${retryAfterSeconds ?: 0} Sekunden wieder möglich."

    override fun quotaExhausted(
        limit: Int?,
        resetLabel: String?,
    ): String {
        val countFragment = limit?.let { " ($it/$it)" } ?: ""
        val resetFragment = resetLabel?.let { "am $it" } ?: "zu Beginn der nächsten Periode"
        return "Ihr kostenloses KI-Kontingent für diesen Monat ist aufgebraucht$countFragment. Es setzt sich $resetFragment zurück."
    }

    override fun forCode(
        code: String?,
        status: Int,
    ): String? = code?.let { codeMessages[it] }
}

public object ErrorStringsEn : ErrorStrings {
    override val sessionExpired = "Your session has expired. Please log in again."
    override val projectNotFound = "Project not found."
    override val serviceUnavailable = "The AI feature is currently unavailable. Please try again later."
    override val networkError = "Connection failed. Your diagram is unchanged — please try again."
    override val validationRejected =
        "No valid diagram could be produced from this description. Your existing diagram is " +
            "unchanged. Try a clearer or simpler description."
    override val compilerDetailPrefix = "Message from the diagram compiler:"

    private val codeMessages = errorCodeMessagesEn()

    override fun rateLimited(retryAfterSeconds: Long?): String =
        "One moment please — too many requests in a short time. Possible again in ${retryAfterSeconds ?: 0} seconds."

    override fun quotaExhausted(
        limit: Int?,
        resetLabel: String?,
    ): String {
        val countFragment = limit?.let { " ($it/$it)" } ?: ""
        val resetFragment = resetLabel?.let { "on $it" } ?: "at the start of the next period"
        return "Your free AI quota for this month is used up$countFragment. It resets $resetFragment."
    }

    override fun forCode(
        code: String?,
        status: Int,
    ): String? = code?.let { codeMessages[it] }
}
